import { parseSourceNumber } from '../../utils/sourceNumeric';

/** Minimum year for comparable DGStats application cost fields (IOU online collection). */
export const SOLAR_COST_SERIES_START_YEAR = 2015;

export const SOLAR_INSTALLER_MIN_SAMPLE = 5;

/** tech_type_cost is mean $/kW DC by residential technology group — not the same contract as all_pv_cost $/W DC. */
export const SOLAR_STORAGE_COST_VIEW_STATUS: 'VERIFIED_PUBLIC' | 'HELD' = 'VERIFIED_PUBLIC';

export type PvCostAggregateRow = {
  year?: number | string | null;
  sector?: string | null;
  cost_per_watt?: number | string | null;
  total_watts?: number | string | null;
  total_cost?: number | string | null;
  num_projects?: number | string | null;
};

export type SolarAggregateCostFact = {
  year: number | null;
  sector: string | null;
  aggregateCostPerW: number | null;
  totalReportedCost: number | null;
  totalAcWatts: number | null;
  representedProjectCount: number | null;
  costDollarBasis: 'nominal';
  rowCount: number;
  aggregationMethod: 'sum_cost_over_sum_watts' | 'capacity_weighted_cpw' | 'unavailable';
};

function rowPassesValidity(row: PvCostAggregateRow): boolean {
  const year = Number(row.year);
  if (!Number.isFinite(year) || year < SOLAR_COST_SERIES_START_YEAR) return false;
  const cpw = parseSourceNumber(row.cost_per_watt);
  const watts = parseSourceNumber(row.total_watts);
  if (cpw == null || cpw <= 0 || watts == null || watts <= 0) return false;
  return true;
}

function filterRows(
  rows: PvCostAggregateRow[],
  options?: { sector?: string; year?: number }
): PvCostAggregateRow[] {
  let filtered = rows.filter(rowPassesValidity);
  if (options?.year != null) {
    filtered = filtered.filter((r) => Number(r.year) === options.year);
  }
  if (options?.sector) {
    const s = options.sector.toLowerCase();
    filtered = filtered.filter((r) => String(r.sector || '').toLowerCase() === s);
  }
  return filtered;
}

function rowHasValidTotalCost(row: PvCostAggregateRow): boolean {
  const tc = parseSourceNumber(row.total_cost);
  const watts = parseSourceNumber(row.total_watts);
  return tc != null && tc > 0 && watts != null && watts > 0;
}

/**
 * CASE 1: all rows have total_cost → SUM(total_cost)/SUM(watts)
 * CASE 2: all rows have cpw+watts → capacity-weighted cpw
 * CASE 3: otherwise unavailable (no mixing partial total_cost with all-row watts)
 */
export function aggregateReportedCostPerWFromRows(
  rows: PvCostAggregateRow[],
  options?: { sector?: string; year?: number }
): SolarAggregateCostFact {
  const filtered = filterRows(rows, options);
  if (!filtered.length) {
    return {
      year: options?.year ?? null,
      sector: options?.sector ?? null,
      aggregateCostPerW: null,
      totalReportedCost: null,
      totalAcWatts: null,
      representedProjectCount: null,
      costDollarBasis: 'nominal',
      rowCount: 0,
      aggregationMethod: 'unavailable',
    };
  }

  let projectCount = 0;
  let hasProjectCount = false;
  for (const row of filtered) {
    const n = parseSourceNumber(row.num_projects);
    if (n != null && n >= 0) {
      projectCount += n;
      hasProjectCount = true;
    }
  }

  const allHaveTotalCost = filtered.every(rowHasValidTotalCost);

  if (allHaveTotalCost) {
    let totalCost = 0;
    let totalWatts = 0;
    for (const row of filtered) {
      totalCost += parseSourceNumber(row.total_cost)!;
      totalWatts += parseSourceNumber(row.total_watts)!;
    }
    return {
      year: options?.year ?? Number(filtered[0].year),
      sector: options?.sector ?? null,
      aggregateCostPerW: totalWatts > 0 ? totalCost / totalWatts : null,
      totalReportedCost: totalCost,
      totalAcWatts: totalWatts,
      representedProjectCount: hasProjectCount ? projectCount : null,
      costDollarBasis: 'nominal',
      rowCount: filtered.length,
      aggregationMethod: 'sum_cost_over_sum_watts',
    };
  }

  let weighted = 0;
  let totalWatts = 0;
  for (const row of filtered) {
    const cpw = parseSourceNumber(row.cost_per_watt);
    const watts = parseSourceNumber(row.total_watts);
    if (cpw == null || cpw <= 0 || watts == null || watts <= 0) {
      return {
        year: options?.year ?? null,
        sector: options?.sector ?? null,
        aggregateCostPerW: null,
        totalReportedCost: null,
        totalAcWatts: null,
        representedProjectCount: hasProjectCount ? projectCount : null,
        costDollarBasis: 'nominal',
        rowCount: filtered.length,
        aggregationMethod: 'unavailable',
      };
    }
    weighted += cpw * watts;
    totalWatts += watts;
  }

  return {
    year: options?.year ?? Number(filtered[0].year),
    sector: options?.sector ?? null,
    aggregateCostPerW: totalWatts > 0 ? weighted / totalWatts : null,
    totalReportedCost: null,
    totalAcWatts: totalWatts,
    representedProjectCount: hasProjectCount ? projectCount : null,
    costDollarBasis: 'nominal',
    rowCount: filtered.length,
    aggregationMethod: 'capacity_weighted_cpw',
  };
}

/** Regression guard: aggregate rows must not be expanded into fake project-level quantiles. */
export function assertNoSyntheticProjectQuantilesFromAggregates(): void {
  const combined = aggregateReportedCostPerWFromRows(
    [
      { year: 2020, sector: 'residential', cost_per_watt: 4, total_watts: 10, num_projects: 100 },
      { year: 2020, sector: 'residential', cost_per_watt: 6, total_watts: 20, num_projects: 1 },
    ],
    { year: 2020, sector: 'residential' }
  );
  const expected = (4 * 10 + 6 * 20) / 30;
  if (combined.aggregateCostPerW == null || Math.abs(combined.aggregateCostPerW - expected) > 1e-9) {
    throw new Error('aggregate cost weighting failed regression');
  }
}
